import React from "react";
import {useHistory, useLocation} from "react-router-dom";
import Roles from "../../shared/models/roles";
import useHighestRole from "./useHighestRole";

/**
 * Extrahiert die Organization ID aus einem URL-Pfad.
 * Der Pfad hat typischerweise das Format: /{organizationId}/bookings/...
 *
 * @param pathname - Der aktuelle URL-Pfad
 * @returns Die Organization ID oder leerer String wenn nicht gefunden
 */
function extractOrganizationIdFromPath(pathname: string): string {
    const segments = pathname.split("/").filter(Boolean);
    return segments[0] || "";
}

/**
 * Abrechnungsstatus einer Buchung. `isSettled` gilt auch, wenn der Beleg selbst durch die Sichtbarkeitsregel
 * „Rechnungen“ verborgen ist (Booking.invoice = null, #709); Abfragen ohne `isSettled` fallen auf die Rechnung zurück.
 */
export const isBookingSettled = (booking?: {isSettled?: boolean | null; invoice?: unknown} | null): boolean => Boolean(booking?.isSettled ?? booking?.invoice);

/**
 * Ermittelt den korrekten URL-Pfad zu den Booking-Details basierend auf:
 * - Settlement-Status der Buchung
 * - Art der Buchung (Lesson Booking vs. regulaere Buchung)
 * - Rolle des angemeldeten Benutzers
 *
 * @param bookingId - ID der Buchung
 * @param isSettled - Ob die Buchung abgerechnet ist
 * @param allowLessonBooking - Ob es sich um eine Lesson-Buchung handelt
 * @param highestRole - Hoechste Rolle des angemeldeten Benutzers
 * @param currentPathname - Optional: Aktueller URL-Pfad zur Extraktion der Organization ID
 * @returns Der vollstaendige URL-Pfad zu den Booking-Details
 */
export function getBookingDetailsPath(
    bookingId: string,
    isSettled: boolean,
    allowLessonBooking: boolean,
    highestRole: Roles,
    currentPathname?: string,
): string {
    const isInstructor = highestRole === Roles.Instructor;

    // Extrahiere Organization ID aus dem aktuellen Pfad (falls angegeben)
    const orgId = currentPathname ? extractOrganizationIdFromPath(currentPathname) : "";
    const urlBase = orgId ? `/${orgId}` : "";
    const bookingsBase = `${urlBase}/bookings`;
    const lessonBookingsBase = `${bookingsBase}/lessonBookings`;
    const instructorBase = `${urlBase}/instructor`;

    // Lesson Booking URLs (gleich fuer alle Rollen)
    const lessonBookingEditUrl = `${lessonBookingsBase}/edit/${bookingId}`;
    const lessonBookingSettledUrl = `${lessonBookingsBase}/settlementDetails/${bookingId}`;

    // Regular Booking URLs (unterschiedlich fuer Instructor)
    const regularBookingEditUrl = isInstructor
        ? `${instructorBase}/edit/${bookingId}`
        : `${bookingsBase}/edit/${bookingId}`;

    const regularBookingSettledUrl = isInstructor
        ? `${instructorBase}/settlementDetails/${bookingId}`
        : `${bookingsBase}/settlementDetails/${bookingId}`;

    // Routing-Logik
    if (allowLessonBooking) {
        return isSettled ? lessonBookingSettledUrl : lessonBookingEditUrl;
    }

    return isSettled ? regularBookingSettledUrl : regularBookingEditUrl;
}

/**
 * Row-Click-Handler für Listen, deren Buchungsnummer-Spalte bereits einen
 * `<Link>` rendert (`renderBookingNumber`).
 *
 * Die große Klickfläche „irgendwo in die Zeile" bleibt erhalten, ohne mit dem
 * Link zu kollidieren: Klicks, die aus einem `<a>` stammen, werden ignoriert —
 * sonst würde der Link navigieren und der Row-Handler direkt danach ein
 * zweites Mal pushen (doppelter History-Eintrag, und bei abweichendem Ziel
 * gewinnt der Row-Handler).
 *
 * Zieladresse ist identisch zum Link, damit abgerechnete Buchungen und
 * Lektionsbuchungen aus beiden Wegen auf derselben Detail-Route landen.
 *
 * Wie der Link übergibt der Handler die aktuelle Liste (inkl. Query-String,
 * z.B. `?tab=Paid`) als `returnTo`, damit „Schließen"/„Speichern" in den
 * Buchungsdetails über `returnBackHelper` wieder in dieser Liste landet
 * statt in der allgemeinen Buchungsliste.
 */
export function useBookingRowClick(): (
    event: React.MouseEvent<HTMLElement>,
    target: {bookingId?: string | null; isSettled?: boolean; allowLessonBooking?: boolean; canceled?: boolean | null},
) => void {
    const history = useHistory();
    const location = useLocation();
    const highestRole = useHighestRole();

    return (event, target) => {
        if (!target.bookingId || target.canceled) return;

        // Klick kam aus einem Link (oder einem Element darin) → der Link
        // navigiert bereits selbst.
        const el = event.target as HTMLElement | null;
        if (el && typeof el.closest === "function" && el.closest("a")) return;

        const returnTo = location.pathname + location.search;
        history.push(
            getBookingDetailsPath(
                target.bookingId,
                Boolean(target.isSettled),
                Boolean(target.allowLessonBooking),
                highestRole,
                location.pathname,
            ),
            {from: {pathname: location.pathname}, returnTo},
        );
    };
}
